import React, { useEffect, useState } from "react"
import { formatNumber } from "helpers/numbers"
import { CardGrowthTreeProps } from "./interface"
import { IntlShape } from "react-intl"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"
import {
	isSafari,
	MapperTitle,
	MapperTooltip,
} from "components/Transactional/GrowthProfiling/Shared/chartUtils"
import { Attributes } from "components/Transactional/GrowthProfiling/Shared/interfaces"
import {
	formattingText,
	DEPARTMENT_MARKET_TITLE,
	CATEGORIES_MARKET_TITLE,
	SUB_CATEGORIES_MARKET_TITLE,
	EMPTY_FILTER,
	DEFAULT,
	LOADING,
} from "components/Shared/GenerateGrowthNode/constants"
import { concatClassNames } from "helpers/jsxHelpers"
import "./index.scss"
import InformationPopover from "../InformationPopover"
import { FormatPercentage } from "./components"

interface FormatAmountProps {
	name: string
	value: Attributes
	intl: IntlShape
}

const checkAbbreviation = (array: Array<string>) => {
	return array.length === 1 ? "" : array[array.length - 1]
}

const FormatAmount: React.FC<FormatAmountProps> = ({ name, value, intl }) => {
	const number = formatNumber(value?.amount ?? 0, intl, 2, true).split(" ")
	name = name ?? DEFAULT
	return (
		<div className="prefix-container">
			<p className="amount bold font-36">
				{formattingText[name].prefix}
				{number[0]}
			</p>
			<p className="prefix font-24">
				{checkAbbreviation(number)} {formattingText[name].suffix}
			</p>
		</div>
	)
}

const MapTitle = (titleKey: string) => {
	titleKey = titleKey ?? LOADING
	return MapperTitle[titleKey as keyof typeof MapperTitle]
}
const MapTooltip = (titleKey: string) => {
	titleKey = titleKey ?? LOADING
	return MapperTooltip[titleKey as keyof typeof MapperTitle]
}

const GrowthCard: React.FC<CardGrowthTreeProps> = ({
	nodeDatum,
	intl,
}: CardGrowthTreeProps) => {
	const { filters } = useFiltersContext()

	const [marketLabel, setMarketLabel] = useState<string>(
		DEPARTMENT_MARKET_TITLE
	)

	const checkSuBCatLabel = () => {
		if (
			filters.categories.length > EMPTY_FILTER &&
			filters.subcategories.length > EMPTY_FILTER
		) {
			setMarketLabel(SUB_CATEGORIES_MARKET_TITLE)
		}
	}

	useEffect(() => {
		if (
			filters.categories.length > EMPTY_FILTER &&
			filters.subcategories.length === EMPTY_FILTER
		) {
			setMarketLabel(CATEGORIES_MARKET_TITLE)
		} else {
			checkSuBCatLabel()
		}
	}, [])

	return (
		<div
			className={concatClassNames([
				"growth-card " + isSafari("isSafari", "isNotSafari"),
			])}
		>
			<div className="information">
				<div className="title ">
					<div className="bold font-24">
						{intl.formatMessage({ id: MapTitle(nodeDatum.name) })}
					</div>
					{InformationPopover({
						text: MapTooltip(nodeDatum.name),
						width: "215px",
					})}
				</div>
				<FormatAmount
					name={nodeDatum.name}
					value={nodeDatum.attributes}
					intl={intl}
				/>
				<FormatPercentage
					value={nodeDatum.attributes?.lastPeriod}
					description="phronesis.transactional.application.growth-profiling.chart.lastperiod"
					showAsPoints={nodeDatum.name === "Reach"}
				/>
			</div>
			<div className="market-container">
				{filters.departments.length > 0 && (
					<FormatPercentage
						value={nodeDatum.attributes?.market}
						description="phronesis.transactional.application.growth-profiling.chart.market"
						label={marketLabel}
					/>
				)}
			</div>
		</div>
	)
}

export default GrowthCard
